import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Shuffle, X, ArrowLeft, Calendar as CalIcon } from "lucide-react";

export default function AdminTournamentControl() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [services, setServices] = useState([]);
  const [scheduling, setScheduling] = useState(null); // match
  const [scoring, setScoring] = useState(null); // match
  const [sched, setSched] = useState({ scheduled_at: "", venue_slug: "cricket-ground", venue_resource: "Ground", duration_hours: 2 });
  const [score, setScore] = useState({ score_a: "", score_b: "", winner_id: "", result_text: "" });

  const load = () => api.get(`/tournaments/${id}`).then((r) => setT(r.data));
  useEffect(() => {
    load();
    api.get("/services").then((r) => setServices(r.data.filter((s) => s.type === "hourly")));
  }, [id]);

  if (!t) return <div className="text-white/40">Loading…</div>;

  const teamById = Object.fromEntries(t.teams.map((x) => [x.id, x]));
  const svc = services.find((s) => s.slug === sched.venue_slug);

  const generate = async (shuffle) => {
    if (!confirm(shuffle ? "Generate bracket with random shuffle?" : "Generate bracket in registration order?")) return;
    try {
      const { data } = await api.post("/admin/tournaments/generate-bracket", { tournament_id: id, shuffle });
      toast.success(`Generated ${data.matches_created} matches`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doSchedule = async () => {
    try {
      await api.post("/admin/tournaments/schedule-match", { match_id: scheduling.id, ...sched });
      toast.success("Scheduled · slot auto-blocked · WhatsApp sent");
      setScheduling(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const doScore = async () => {
    try {
      await api.post("/admin/tournaments/update-score", {
        match_id: scoring.id,
        score_a: score.score_a, score_b: score.score_b,
        winner_id: score.winner_id, result_text: score.result_text,
        status: "completed",
      });
      toast.success("Score updated & winner advanced");
      setScoring(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-tournament-control">
      <Link to="/admin/tournaments" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-4 font-mono uppercase tracking-widest">
        <ArrowLeft size={14} /> All Tournaments
      </Link>
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Match Control · {t.sport}</div>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none">{t.name}</h1>
        <div className="flex gap-2">
          <button onClick={() => generate(true)} data-testid="gen-bracket-random" className="bg-brand-blue text-white px-5 py-2 text-xs uppercase font-bold tracking-widest flex items-center gap-2">
            <Shuffle size={12} /> Random Seed
          </button>
          <button onClick={() => generate(false)} data-testid="gen-bracket-order" className="border border-white/20 px-5 py-2 text-xs uppercase font-bold tracking-widest hover:border-white/50">
            Fixed Order
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-3 gap-3 mb-8">
        <Stat label="Registered Teams" value={t.teams.length} />
        <Stat label="Matches" value={t.matches.length} />
        <Stat label="Completed" value={t.matches.filter((m) => m.status === "completed").length} />
      </div>

      <div className="border border-white/12 bg-[#131A2B] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="font-mono text-xs uppercase tracking-widest text-white/40 border-b border-white/12">
              <th className="text-left p-4">R#</th>
              <th className="text-left p-4">Match</th>
              <th className="text-left p-4">When</th>
              <th className="text-left p-4">Venue</th>
              <th className="text-left p-4">Score</th>
              <th className="text-left p-4">Status</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {t.matches.map((m) => (
              <tr key={m.id} data-testid={`match-row-${m.id}`} className="border-b border-white/5">
                <td className="p-4 font-mono">{m.round}·{m.match_number}</td>
                <td className="p-4">
                  {teamById[m.team_a_id]?.name || "TBD"} <span className="text-white/40">vs</span> {teamById[m.team_b_id]?.name || "TBD"}
                </td>
                <td className="p-4 text-xs font-mono">{m.scheduled_at?.slice(0, 16).replace("T", " ") || "—"}</td>
                <td className="p-4 text-xs">{m.venue_resource || "—"}</td>
                <td className="p-4 font-mono brand-blue">{m.score_a ? `${m.score_a} — ${m.score_b}` : "—"}</td>
                <td className="p-4 text-xs uppercase font-mono tracking-widest">{m.status}</td>
                <td className="p-4 text-right space-x-2">
                  {m.team_a_id && m.team_b_id && m.status !== "completed" && (
                    <>
                      <button
                        onClick={() => {
                          setScheduling(m);
                          setSched({
                            scheduled_at: m.scheduled_at || new Date().toISOString().slice(0, 16),
                            venue_slug: m.venue_slug || "cricket-ground",
                            venue_resource: m.venue_resource || "Ground",
                            duration_hours: m.duration_hours || 2,
                          });
                        }}
                        data-testid={`schedule-${m.id}`}
                        className="border border-brand-orange text-brand-orange px-3 py-1.5 text-xs uppercase tracking-widest"
                      >
                        <CalIcon size={12} className="inline mr-1" /> Schedule
                      </button>
                      <button
                        onClick={() => {
                          setScoring(m);
                          setScore({ score_a: m.score_a || "", score_b: m.score_b || "", winner_id: m.winner_id || "", result_text: m.result_text || "" });
                        }}
                        data-testid={`score-${m.id}`}
                        className="bg-brand-blue text-white px-3 py-1.5 text-xs uppercase tracking-widest font-bold"
                      >
                        Score
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {t.matches.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-white/40">No matches. Generate bracket above.</td></tr>}
          </tbody>
        </table>
      </div>

      {scheduling && (
        <Modal onClose={() => setScheduling(null)} title="Schedule Match">
          <div className="mb-4 text-sm text-white/60">
            {teamById[scheduling.team_a_id]?.name} vs {teamById[scheduling.team_b_id]?.name}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input type="datetime-local" value={sched.scheduled_at} onChange={(e) => setSched({ ...sched, scheduled_at: e.target.value })} data-testid="sched-datetime" className="bg-transparent border border-white/20 py-3 px-4 col-span-2" />
            <select value={sched.venue_slug} onChange={(e) => setSched({ ...sched, venue_slug: e.target.value, venue_resource: services.find(s => s.slug === e.target.value)?.resources[0] || "" })} data-testid="sched-venue" className="bg-transparent border border-white/20 py-3 px-4">
              {services.map((s) => <option key={s.slug} value={s.slug} className="bg-[#131A2B]">{s.name}</option>)}
            </select>
            <select value={sched.venue_resource} onChange={(e) => setSched({ ...sched, venue_resource: e.target.value })} data-testid="sched-resource" className="bg-transparent border border-white/20 py-3 px-4">
              {svc?.resources.map((r) => <option key={r} value={r} className="bg-[#131A2B]">{r}</option>)}
            </select>
            <input type="number" min="1" max="10" value={sched.duration_hours} onChange={(e) => setSched({ ...sched, duration_hours: parseInt(e.target.value) })} placeholder="Duration hrs" className="bg-transparent border border-white/20 py-3 px-4 col-span-2" />
          </div>
          <div className="mt-4 text-xs text-white/50 font-mono">Auto-blocks slot on the rental engine. Sends WhatsApp to both captains.</div>
          <button onClick={doSchedule} data-testid="sched-submit" className="mt-6 w-full bg-brand-blue text-white py-3 font-bold uppercase tracking-widest">Confirm & Notify</button>
        </Modal>
      )}

      {scoring && (
        <Modal onClose={() => setScoring(null)} title="Match Score">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-mono uppercase text-white/40 mb-1">{teamById[scoring.team_a_id]?.name}</div>
                <input value={score.score_a} onChange={(e) => setScore({ ...score, score_a: e.target.value })} placeholder="e.g. 152/4" data-testid="score-a" className="w-full bg-transparent border border-white/20 py-3 px-4" />
              </div>
              <div>
                <div className="text-xs font-mono uppercase text-white/40 mb-1">{teamById[scoring.team_b_id]?.name}</div>
                <input value={score.score_b} onChange={(e) => setScore({ ...score, score_b: e.target.value })} placeholder="e.g. 148/8" data-testid="score-b" className="w-full bg-transparent border border-white/20 py-3 px-4" />
              </div>
            </div>
            <select value={score.winner_id} onChange={(e) => setScore({ ...score, winner_id: e.target.value })} data-testid="score-winner" className="w-full bg-transparent border border-white/20 py-3 px-4">
              <option value="">Select Winner</option>
              <option value={scoring.team_a_id}>{teamById[scoring.team_a_id]?.name}</option>
              <option value={scoring.team_b_id}>{teamById[scoring.team_b_id]?.name}</option>
            </select>
            <input value={score.result_text} onChange={(e) => setScore({ ...score, result_text: e.target.value })} placeholder="e.g. Won by 4 wickets" className="w-full bg-transparent border border-white/20 py-3 px-4" />
            <button onClick={doScore} data-testid="score-submit" className="w-full bg-brand-blue text-white py-3 font-bold uppercase tracking-widest">Save & Advance Winner</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-white/12 bg-[#131A2B] p-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">{label}</div>
      <div className="font-display text-3xl font-black brand-blue">{value}</div>
    </div>
  );
}

function Modal({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#131A2B] border border-white/12 p-8 max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-2xl uppercase font-black">{title}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
